import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import FormValidationError from '@shared/form/form-validation-error/form-validation-error';
import FormCheckboxComponents from '@shared/form/form-checkbox/form-checkbox.components';
import FieldLabel from '@shared/form/field-label/field-label';
import { split } from 'lodash';
import { useTranslation } from 'react-i18next';
import FileLabelComponents from '@shared/form/field-label/field-label.components';

interface FormCheckboxProps {
  label: string;
  name: string;
  errorMessage: string;
  isRequired: boolean;
  link?: { content: string; path: string };
}
const {
  Container,
  Checkbox,
  CustomInput,
  StyledLabel,
  CheckmarkField,
  Checkmark,
  DecriptionLink
} = FormCheckboxComponents;
const { RequiredMark } = FileLabelComponents;

const FormCheckbox: React.FunctionComponent<FormCheckboxProps> = ({
  label,
  link,
  name,
  errorMessage,
  isRequired
}) => {
  const { register, setValue, trigger } = useFormContext();
  const [isActive, setIsActive] = useState(false);
  const { t } = useTranslation();

  const description = useMemo(() => {
    if (!link?.content || !link?.path) {
      return <FieldLabel isRequired={isRequired} text={label} />;
    }
    const descriptionLink = (
      <DecriptionLink
        href={link.path}
        target="_blank"
        rel="noreferrer"
        className="ucf-link">
        {t(link.content)}
      </DecriptionLink>
    );

    const [head, tail] = split(label, '{{link}}');

    return (
      <>
        {t(head)}
        {descriptionLink}
        {t(tail)}
        {isRequired && <RequiredMark>*</RequiredMark>}
      </>
    );
  }, [isRequired, label, link?.content, link?.path, t]);

  const toggleCheckbox = useCallback(() => {
    setValue(name, !isActive);
    setIsActive((prevVal) => !prevVal);
    trigger(name);
  }, [isActive, name, setValue, trigger]);

  return (
    <Container>
      <Checkbox>
        <CheckmarkField isActive={isActive} onClick={toggleCheckbox}>
          <Checkmark isActive={isActive} />
        </CheckmarkField>
        <StyledLabel>
          {description}
          <CustomInput
            type="checkbox"
            {...register(name)}
            checked={isActive}
            onChange={(): void => {}}
          />
        </StyledLabel>
      </Checkbox>
      <FormValidationError errorMessage={errorMessage} />
    </Container>
  );
};

export default FormCheckbox;
